import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { SavedFilterPermissionPicker } from './SavedFilterPermissionPicker'
import type { SavedFilterPermission } from '@/lib/types/savedFilter'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useSavedFilterMutations } from '@/lib/db/rxdb/hooks'

interface ChangePermissionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filterId: string
  currentPermission: SavedFilterPermission
}

export function ChangePermissionDialog({
  open,
  onOpenChange,
  filterId,
  currentPermission,
}: ChangePermissionDialogProps) {
  const { t } = useTranslation('tasks')
  const { t: tCommon } = useTranslation('common')
  const [permission, setPermission] = useState<SavedFilterPermission>(currentPermission)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { updateSavedFilter } = useSavedFilterMutations()

  useEffect(() => {
    if (open) setPermission(currentPermission)
  }, [open, currentPermission])

  const handleSave = async () => {
    if (permission === currentPermission) {
      onOpenChange(false)
      return
    }

    setIsSubmitting(true)
    try {
      await updateSavedFilter(filterId, { permission })
      toast.success(t('savedFilters.toast.permissionUpdated'))
      onOpenChange(false)
    } catch {
      toast.error(t('savedFilters.toast.permissionFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t('savedFilters.permissionDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('savedFilters.permissionDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-2">
          <Label>{t('savedFilters.permissionDialog.whoCanSee')}</Label>
          <SavedFilterPermissionPicker
            value={permission}
            onChange={setPermission}
            disabled={isSubmitting}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {tCommon('actions.cancel')}
          </Button>
          <Button
            onClick={() => { void handleSave() }}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('savedFilters.permissionDialog.saving') : tCommon('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
